import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, Mail, ShieldCheck } from "lucide-react";

const ROLES = [
  { value: "chef", label: "Chef" },
  { value: "comandante", label: "Comandante" },
  { value: "armatore", label: "Armatore (sola lettura)" },
];

export default function Utenti() {
  const { user } = useAuth();
  const location = useLocation();
  const isManager = user && (user.role === "chef" || user.role === "comandante");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "chef" });
  const [creating, setCreating] = useState(false);
  const [emailCheck, setEmailCheck] = useState({ status: "idle", reason: "" });
  const nameRef = useRef(null);

  useEffect(() => {
    if (location.state?.focusCreate && nameRef.current) {
      nameRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      nameRef.current.focus();
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const verifyEmail = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email) { setEmailCheck({ status: "idle", reason: "" }); return; }
    setEmailCheck({ status: "checking", reason: "" });
    try {
      const r = await api.post("/users/verify-email", { email });
      if (r.data.deliverable) setEmailCheck({ status: "ok", reason: r.data.unchecked ? "" : "Dominio valido ✓" });
      else setEmailCheck({ status: "bad", reason: r.data.reason || "Email non valida" });
    } catch {
      setEmailCheck({ status: "idle", reason: "" });
    }
  };

  useEffect(() => {
    const st = location.state;
    if (st && (st.prefillName || st.prefillEmail)) {
      setForm((f) => ({ ...f, name: st.prefillName || "", email: st.prefillEmail || "", role: "armatore" }));
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (isManager) load();
    else setLoading(false);
  }, [isManager]);

  if (!isManager) {
    return (
      <div data-testid="utenti-denied" className="rounded-2xl bg-white p-10 text-center text-slate-500">
        Sezione riservata a Chef e Comandante.
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Compila nome, email e una password di almeno 6 caratteri");
      return;
    }
    if (emailCheck.status === "bad") {
      toast.error(emailCheck.reason || "Indirizzo email non valido");
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", { ...form, email: form.email.trim().toLowerCase() });
      toast.success("Utente creato");
      setForm({ name: "", email: "", password: "", role: "chef" });
      setEmailCheck({ status: "idle", reason: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}/role`, { role });
      toast.success("Ruolo aggiornato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const sendWelcome = async (u) => {
    if (!window.confirm(`Inviare l'email di benvenuto a ${u.email}?\n\nVerrà impostata una NUOVA password sicura e inviata via email. La password precedente non sarà più valida.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/welcome`);
      if (r.data.email_sent) toast.success(`Email di benvenuto inviata a ${u.email}`);
      else toast.error("Invio non riuscito (email non valida?)");
    } catch { toast.error("Invio non riuscito"); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Nuova password per ${u.email} (min 6 caratteri):`);
    if (!pw) return;
    if (pw.length < 6) {
      toast.error("Password troppo corta");
      return;
    }
    try {
      await api.post(`/users/${u.id}/reset-password`, { password: pw });
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'accesso di ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-6" data-testid="utenti-page">
      <div>
        <h1 className="font-head text-3xl font-bold text-navy">Gestione utenti</h1>
        <p className="mt-1 text-sm text-slate-500">Crea gli accessi per l'equipaggio e assegna i ruoli.</p>
      </div>

      <div data-testid="anti-spam-banner" className="flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/10 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
        <div className="text-sm text-navy">
          <span className="font-semibold">Protezione Anti-Spam intelligente attiva.</span>{" "}
          Il sistema riconosce automaticamente le email false o inesistenti e le blocca prima
          dell'invio, così ogni invito di accesso arriva solo a indirizzi reali e verificati.
        </div>
      </div>

      <form onSubmit={create} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2 lg:grid-cols-4">
        <input ref={nameRef} data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
        <div className="flex flex-col">
          <input
            data-testid="user-email-input"
            type="email"
            value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); if (emailCheck.status !== "idle") setEmailCheck({ status: "idle", reason: "" }); }}
            onBlur={verifyEmail}
            placeholder="Email"
            className={`h-11 rounded-lg border-2 px-3 outline-none ${emailCheck.status === "bad" ? "border-red-400 focus:border-red-500" : emailCheck.status === "ok" ? "border-emerald-400 focus:border-emerald-500" : "border-slate-200 focus:border-gold"}`}
          />
          {emailCheck.status === "checking" && <span data-testid="email-check-msg" className="mt-1 text-xs text-slate-400">Verifica indirizzo…</span>}
          {emailCheck.status === "bad" && <span data-testid="email-check-msg" className="mt-1 text-xs text-red-600">{emailCheck.reason}</span>}
          {emailCheck.status === "ok" && emailCheck.reason && <span data-testid="email-check-msg" className="mt-1 text-xs text-emerald-600">{emailCheck.reason}</span>}
        </div>
        <input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6)" className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
        <div className="flex gap-2">
          <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 flex-1 rounded-lg border-2 border-slate-200 px-2 outline-none focus:border-gold">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button data-testid="user-create-btn" type="submit" disabled={creating || emailCheck.status === "bad"} className="flex h-11 items-center gap-2 rounded-lg bg-navy px-4 font-semibold text-white hover:bg-navy-hover disabled:opacity-60">
            <UserPlus className="h-4 w-4" /> Crea
          </button>
        </div>
      </form>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Caricamento…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Ruolo</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-navy">
                    {u.name}
                    {u.auth_provider === "google" && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">Google</span>}
                    {u.id === user.id && <span className="ml-2 rounded bg-gold/20 px-1.5 py-0.5 text-[10px] text-navy">Tu</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`user-role-${u.email}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === user.id} className="rounded-lg border border-slate-200 px-2 py-1.5 disabled:opacity-50">
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`user-welcome-${u.email}`} onClick={() => sendWelcome(u)} title="Invia email di benvenuto" className="rounded-lg bg-navy p-2 text-white hover:bg-navy-hover"><Mail className="h-4 w-4" /></button>
                      <button data-testid={`user-reset-${u.email}`} onClick={() => resetPw(u)} title="Reimposta password" className="rounded-lg bg-slate-100 p-2 text-navy hover:bg-slate-200"><KeyRound className="h-4 w-4" /></button>
                      <button data-testid={`user-delete-${u.email}`} onClick={() => remove(u)} disabled={u.id === user.id} title="Elimina" className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100 disabled:opacity-40"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
